import { type components, createDriveClient } from "@vault/client";

export const client = createDriveClient({ baseUrl: "/api/v1" });

export type FileNode = components["schemas"]["Node"];
export type NodeAncestor = FileNode["ancestors"][number];
export type NodeCreate = components["schemas"]["NodeCreate"];
export type NodePatch = components["schemas"]["NodePatch"];
export type FileVersion = components["schemas"]["FileVersion"];
export type Audience = components["schemas"]["Audience"];
export type Usage = components["schemas"]["Usage"];
export type CostLine = components["schemas"]["CostLine"];
export type TopFile = components["schemas"]["TopFile"];
export type UsageAlert = components["schemas"]["UsageAlert"];
export type DayUsage = components["schemas"]["DayUsage"];
export type MonthUsage = components["schemas"]["MonthUsage"];
export type Me = components["schemas"]["Me"];
export type UploadResult = components["schemas"]["UploadResult"];
export type Group = components["schemas"]["Group"];
export type Principal = components["schemas"]["Principal"];
export type ShareLink = components["schemas"]["ShareLink"];
export type ShareCreate = components["schemas"]["ShareCreate"];
export type ApiToken = components["schemas"]["ApiToken"];
export type TokenCreate = components["schemas"]["TokenCreate"];
export type TokenCreated = components["schemas"]["TokenCreated"];
export type Scope = components["schemas"]["Scope"];
export type ActivityEntry = components["schemas"]["ActivityEntry"];
export type ActivityActor = components["schemas"]["ActivityActor"];
export type ActivityFilter = components["schemas"]["ActivityFilter"];
export type ActivityList = components["schemas"]["ActivityList"];
export type MultipartUpload = components["schemas"]["MultipartUpload"];
export type UploadPart = components["schemas"]["UploadPart"];

export class ApiError extends Error {
  readonly status: number;
  readonly code: string;

  constructor(status: number, code: string, message: string) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.code = code;
  }
}

export const signIn = (): Promise<never> => {
  window.location.assign(`/auth/login?return=${encodeURIComponent(window.location.href)}`);
  return new Promise(() => {});
};

export const errorOf = (status: number, body: unknown): ApiError => {
  const detail =
    typeof body === "object" && body !== null && "error" in body
      ? (body as { error: { code?: string; message?: string } }).error
      : undefined;
  return new ApiError(
    status,
    detail?.code ?? "http_error",
    detail?.message ?? `The drive answered ${status}.`,
  );
};

type Result<D> = { data?: D; error?: unknown; response: Response };

export const unwrap = async <D>(request: Promise<Result<D>>): Promise<D> => {
  const { data, error, response } = await request;
  if (response.status === 401) return signIn();
  if (!response.ok) throw errorOf(response.status, error);
  return data as D;
};
